import { useCallback, useSyncExternalStore } from 'react'

/**
 * Persisted UI state (sidebar collapse, view toggles), read via useSyncExternalStore so
 * SSR and hydration stay consistent: the server render and hydration both see `initial`,
 * then React re-reads the real stored value immediately after mount. This is the
 * sanctioned shape for external mutable sources — the prototype's version read
 * localStorage inside a useState initializer (throws during SSR), and a setState-in-
 * an-effect port trips react-hooks/set-state-in-effect. Don't regress to either.
 */

const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  // Same-tab writes go through emit(); the storage event covers other tabs.
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

/** getSnapshot must return a stable reference for unchanged data, or React re-renders forever. */
const parseCache = new Map<string, { raw: string; parsed: unknown }>()

function readKey<T>(key: string, initial: T): T {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(key)
  } catch {
    return initial
  }
  if (raw === null) return initial
  const cached = parseCache.get(key)
  if (cached && cached.raw === raw) return cached.parsed as T
  try {
    const parsed = JSON.parse(raw) as T
    parseCache.set(key, { raw, parsed })
    return parsed
  } catch {
    // Unparseable garbage in the key falls back to the default — never worth crashing for.
    return initial
  }
}

export function useLocalStorage<T>(key: string, initial: T) {
  const value = useSyncExternalStore(
    subscribe,
    () => readKey(key, initial),
    () => initial,
  )

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(readKey(key, initial)) : next
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved))
      } catch {
        // Blocked storage (private mode/quota): the value can't change, since storage is
        // the store. The UI control goes inert rather than lying about persistence.
      }
      emit()
    },
    [key, initial],
  )

  return [value, set] as const
}
